import React, { useContext, useEffect, useState } from 'react';
import { AuthContext } from '../../context/AuthContext';
import { DEFAULT_ABOUT_PROFILES } from '../../lib/aboutProfiles';

const editableFields = [
  ['name', 'Name', 'text'],
  ['role', 'Role', 'text'],
  ['age', 'Age', 'number'],
  ['gender', 'Gender', 'text'],
  ['bio', 'Short bio', 'textarea'],
  ['detail', 'Profile details', 'textarea'],
  ['image_url', 'Image URL (optional)', 'url'],
  ['profile_url', 'Profile link (optional)', 'url'],
];

function moveProfile(profiles, index, direction) {
  const target = index + direction;
  if (target < 0 || target >= profiles.length) return profiles;
  const next = [...profiles];
  const [profile] = next.splice(index, 1);
  next.splice(target, 0, profile);
  return next;
}

export default function AdminAboutPage() {
  const { token } = useContext(AuthContext);
  const [profiles, setProfiles] = useState(DEFAULT_ABOUT_PROFILES);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;
    const loadProfiles = async () => {
      setLoading(true);
      setError('');
      try {
        const response = await fetch('/api/admin/about', { headers: { 'x-auth-token': token } });
        const data = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(data.msg || 'About Us profiles could not be loaded.');
        if (active && Array.isArray(data)) setProfiles(data);
      } catch (loadError) {
        if (active) setError(loadError.message);
      } finally {
        if (active) setLoading(false);
      }
    };
    if (token) loadProfiles();
    return () => { active = false; };
  }, [token]);

  const handleChange = (index, field, value) => {
    setProfiles((current) => current.map((profile, profileIndex) => (
      profileIndex === index ? { ...profile, [field]: value } : profile
    )));
  };

  const handleMove = (index, direction) => {
    setProfiles((current) => moveProfile(current, index, direction));
    setMessage('');
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage('');
    setError('');
    try {
      const response = await fetch('/api/about', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'x-auth-token': token },
        body: JSON.stringify({ profiles: profiles.map((profile, index) => ({ ...profile, sort_order: index + 1 })) }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.msg || 'About Us profiles could not be saved.');
      setProfiles(data.profiles || profiles);
      setMessage('About Us profiles saved.');
    } catch (saveError) {
      setError(saveError.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="admin-about-page container-fluid py-4">
      <div className="admin-cms-page-header d-flex justify-content-between align-items-center mb-4">
        <div>
          <p className="admin-about-page__eyebrow">Store content</p>
          <h2 className="brand-font m-0">About Us profiles</h2>
          <span className="text-muted small">Edit the four people shown on the public About Us page.</span>
        </div>
        <button className="btn btn-primary-custom rounded-pill px-4" type="button" onClick={handleSave} disabled={saving || loading}>
          {saving ? 'Saving…' : 'Save profiles'}
        </button>
      </div>

      <div className="admin-about-page__note">
        Hella and Peti start with editable profile copy. Update any detail before publishing if you want the page to match their own words.
      </div>

      {message && <div className="alert alert-success py-2 px-3 small">{message}</div>}
      {error && <div className="alert alert-danger py-2 px-3 small">{error}</div>}

      {loading ? (
        <div className="admin-about-page__loading" role="status">Loading About Us profiles…</div>
      ) : (
        <div className="admin-about-grid">
          {profiles.map((profile, index) => (
            <section className="admin-about-card" key={profile.slug || index}>
              <div className="admin-about-card__header">
                <div>
                  <span className="admin-about-card__number">#{index + 1}</span>
                  <h3>{profile.name || 'Unnamed profile'}</h3>
                </div>
                <label className="form-check form-switch admin-about-card__visibility">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    checked={profile.enabled !== false}
                    onChange={(event) => handleChange(index, 'enabled', event.target.checked)}
                  />
                  <span className="form-check-label">Visible</span>
                </label>
                <div className="admin-about-card__order-actions" aria-label={`Reorder ${profile.name || 'profile'}`}>
                  <button type="button" className="btn btn-sm btn-outline-custom" onClick={() => handleMove(index, -1)} disabled={index === 0}>↑ Up</button>
                  <button type="button" className="btn btn-sm btn-outline-custom" onClick={() => handleMove(index, 1)} disabled={index === profiles.length - 1}>↓ Down</button>
                </div>
              </div>

              <div className="row g-3">
                {editableFields.map(([field, label, type]) => (
                  <div className={type === 'textarea' ? 'col-12' : 'col-12 col-md-6'} key={field}>
                    <label className="form-label" htmlFor={`about-${profile.slug}-${field}`}>{label}</label>
                    {type === 'textarea' ? (
                      <textarea
                        id={`about-${profile.slug}-${field}`}
                        className="form-control"
                        rows={field === 'bio' ? 3 : 4}
                        value={profile[field] ?? ''}
                        onChange={(event) => handleChange(index, field, event.target.value)}
                      />
                    ) : (
                      <input
                        id={`about-${profile.slug}-${field}`}
                        className="form-control"
                        type={type}
                        min={type === 'number' ? 0 : undefined}
                        max={type === 'number' ? 130 : undefined}
                        value={profile[field] ?? ''}
                        onChange={(event) => handleChange(index, field, event.target.value)}
                      />
                    )}
                  </div>
                ))}
              </div>
            </section>
          ))}
        </div>
      )}
    </div>
  );
}
